// CryptoService.js
import CryptoJS from 'crypto-js';

export default class CryptoService {
  // 128-bit key (16 bytes)
  static key = CryptoJS.enc.Utf8.parse('2n9CHErypUK3fm6F'); // same key as Dart

  /**
   * Encrypts a given text or object using AES-128-CBC
   * @param {*} text String or Object
   * @returns {string} Base64 encoded IV + ciphertext
   */
  static encrypt(text) {
    // If object, convert to JSON string
    const textToEncrypt = (typeof text === 'string')
      ? text
      : JSON.stringify(text);

    // Generate random 16-byte IV
    const iv = CryptoJS.lib.WordArray.random(16);

    // Encrypt
    const encrypted = CryptoJS.AES.encrypt(
      textToEncrypt,
      CryptoService.key,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );

    // Combine IV + ciphertext bytes
    const ivWords = iv.words;
    const ivBytes = CryptoService.wordArrayToUint8Array(iv);

    const ciphertextBytes = CryptoService.wordArrayToUint8Array(encrypted.ciphertext);

    const combined = new Uint8Array(ivBytes.length + ciphertextBytes.length);
    combined.set(ivBytes, 0);
    combined.set(ciphertextBytes, ivBytes.length);

    // Encode to Base64
    return CryptoService.uint8ArrayToBase64(combined);
  }

  /**
   * Decrypts a Base64 encoded string (containing IV + ciphertext)
   * @param {string} base64Text 
   * @returns {string} Plaintext
   */
  static decrypt(base64Text) {
    // Decode Base64
    const combined = CryptoService.base64ToUint8Array(base64Text);

    // Extract IV (first 16 bytes)
    const ivBytes = combined.slice(0, 16);
    const ciphertextBytes = combined.slice(16);

    const iv = CryptoJS.lib.WordArray.create(ivBytes);
    const ciphertext = CryptoJS.lib.WordArray.create(ciphertextBytes);

    // Decrypt
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext },
      CryptoService.key,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );

    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  // Helpers: WordArray <-> Uint8Array

  static wordArrayToUint8Array(wordArray) {
    const words = wordArray.words;
    const sigBytes = wordArray.sigBytes;

    const u8 = new Uint8Array(sigBytes);
    let idx = 0;
    for (let i = 0; i < sigBytes; i++) {
      /* eslint-disable no-bitwise */
      const byte = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
      u8[idx++] = byte;
    }
    return u8;
  }

  static uint8ArrayToBase64(u8) {
    let binary = '';
    u8.forEach((b) => binary += String.fromCharCode(b));
    return btoa(binary);
  }

  static base64ToUint8Array(base64) {
    const binary = atob(base64);
    const u8 = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      u8[i] = binary.charCodeAt(i);
    }
    return u8;
  }
}
